import { Box, Drawer, useMediaQuery, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router';

import type { AuthEnvironment } from '../auth/types';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';

const expandedWidth = 264;
const collapsedWidth = 80;
const mobileWidth = 288;

export interface MainLayoutProps {
  readonly buildSha?: string;
  readonly environment: AuthEnvironment;
  readonly environmentLabel: string;
  readonly version?: string;
}

export function MainLayout({
  buildSha = 'local',
  environment,
  environmentLabel,
  version = 'development',
}: MainLayoutProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleNavigation = () => {
    if (mobile) setMobileOpen((open) => !open);
    else setDesktopCollapsed((collapsed) => !collapsed);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {mobile ? (
        mobileOpen ? (
          <Drawer
            onClose={() => setMobileOpen(false)}
            open
            slotProps={{ paper: { sx: { width: mobileWidth } } }}
            variant="temporary"
          >
            <Sidebar
              buildSha={buildSha}
              environmentLabel={environmentLabel}
              mobile
              onClose={() => setMobileOpen(false)}
              version={version}
            />
          </Drawer>
        ) : null
      ) : (
        <Box
          component="aside"
          sx={{
            borderRight: 1,
            borderColor: 'navigation.divider',
            flex: `0 0 ${desktopCollapsed ? collapsedWidth : expandedWidth}px`,
            position: 'sticky',
            top: 0,
            height: '100vh',
            transition: theme.transitions.create('flex-basis'),
          }}
        >
          <Sidebar
            buildSha={buildSha}
            collapsed={desktopCollapsed}
            environmentLabel={environmentLabel}
            version={version}
          />
        </Box>
      )}

      <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', minWidth: 0 }}>
        <Topbar
          collapsed={desktopCollapsed}
          environment={environment}
          mobile={mobile}
          onNavigationToggle={toggleNavigation}
        />
        <Box
          aria-label={t('shell.mainContent')}
          component="main"
          sx={{ flex: 1, p: { xs: 2, sm: 3, lg: 4 } }}
        >
          <Outlet context={environment} />
        </Box>
      </Box>
    </Box>
  );
}
